/**
 * Field save-queue telemetry (Issue #693) — record WHY a save was diverted to
 * the offline outbox, so we can prove false-offlines dropped and find the real
 * dead zones.
 *
 * THE ONE SUBTLETY: the event we most want — "queued because offline" — happens
 * exactly when we can't reach the network. So this is deliberately NOT a naive
 * fetch, and it must NEVER route through `saveThroughOutbox` / `queueOfflineAction`
 * (a failed telemetry send queuing a telemetry-about-queuing event would recurse
 * forever). Instead each event appends to a bounded localStorage ring buffer,
 * and the batch flushes on the same reconnect triggers the drain already uses
 * (`online` / `focus` / `visibilitychange`). Best-effort: if a flush can't land
 * the buffer is kept and retried on the next trigger, so an offline-moment event
 * is captured locally and posts the instant the tech is back on signal.
 *
 * No payload bodies, no customer PII — only an id-stripped endpoint template,
 * the reason, and coarse network context.
 */
import { getToken } from "./api";
import { appVersion } from "./app-version";

/** Must match the CHECK constraint on `field_save_queue_events.reason` and the
 *  server's `REASONS`. */
export type QueueReason = "offline_confirmed" | "write_timeout" | "write_network_error" | "write_http_5xx";

export interface QueueTelemetryContext {
    url: string;
    method: string;
    jobId?: string | null;
}

interface QueueEvent {
    reason: QueueReason;
    /** Epoch ms on the device when the save queued. */
    eventAt: number;
    endpoint: string | null;
    method: string;
    jobId: string | null;
    navigatorOnline: boolean | null;
    effectiveType: string | null;
    appVersion: string | null;
}

const BUFFER_KEY = "gwc_queue_telemetry_v1";
/** The buffer (and each flushed batch) is bounded so a long dead zone with many
 *  saves can't grow localStorage without limit; the server rejects a larger
 *  batch too. */
const MAX_BUFFER = 100;
const FLUSH_URL = "/api/telemetry/queue-events";

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Reduce a request URL to a PII-free path template: drop origin + query/hash,
 *  replace UUIDs and long numeric ids with `:id`. e.g.
 *  `/api/jobs/6f9…/notes?x=1` → `/api/jobs/:id/notes`. */
export function stripEndpoint(url: string): string | null {
    try {
        const path = url.split(/[?#]/)[0] ?? url;
        return path
            .replace(/^https?:\/\/[^/]+/i, "")
            .replace(UUID_RE, ":id")
            .replace(/\/\d{3,}(?=\/|$)/g, "/:id");
    } catch {
        return null;
    }
}

function effectiveType(): string | null {
    try {
        const conn = (navigator as unknown as { connection?: { effectiveType?: string } }).connection;
        return conn?.effectiveType ?? null;
    } catch {
        return null;
    }
}

function readBuffer(): QueueEvent[] {
    try {
        const raw = localStorage.getItem(BUFFER_KEY);
        if (!raw) return [];
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as QueueEvent[]) : [];
    } catch {
        return [];
    }
}

function writeBuffer(events: QueueEvent[]): void {
    try {
        localStorage.setItem(BUFFER_KEY, JSON.stringify(events));
    } catch {
        // localStorage full / unavailable (private mode) — best-effort, drop it.
    }
}

/**
 * Record one queue event. NEVER throws and NEVER hits the network — it only
 * appends to the local buffer, so a save is never slowed or broken by telemetry.
 */
export function recordQueueEvent(reason: QueueReason, ctx: QueueTelemetryContext): void {
    try {
        const event: QueueEvent = {
            reason,
            eventAt: Date.now(),
            endpoint: stripEndpoint(ctx.url),
            method: (ctx.method || "").toUpperCase().slice(0, 12),
            jobId: ctx.jobId ?? null,
            navigatorOnline: typeof navigator === "undefined" ? null : navigator.onLine,
            effectiveType: effectiveType(),
            appVersion: appVersion(),
        };
        const buf = readBuffer();
        buf.push(event);
        while (buf.length > MAX_BUFFER) buf.shift();
        writeBuffer(buf);
    } catch {
        // Telemetry must never break a save — swallow everything.
    }
}

let flushInFlight = false;

/**
 * Post the buffered batch. On success, drop exactly the events we sent (any
 * appended during the request are preserved — the buffer only grows at the
 * end). On any failure the buffer is kept for the next trigger. Best-effort;
 * never throws.
 */
export async function flushQueueTelemetry(): Promise<void> {
    if (flushInFlight) return;
    const batch = readBuffer().slice(0, MAX_BUFFER);
    if (batch.length === 0) return;
    flushInFlight = true;
    try {
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        const token = getToken();
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const res = await fetch(FLUSH_URL, {
            method: "POST",
            headers,
            body: JSON.stringify({ events: batch }),
            keepalive: true,
        });
        if (res.ok) {
            writeBuffer(readBuffer().slice(batch.length));
        }
        // Non-ok (e.g. offline-proxied 5xx) → keep the buffer and retry later.
    } catch {
        // Offline / network error — keep the buffer, retry on the next trigger.
    } finally {
        flushInFlight = false;
    }
}

if (typeof window !== "undefined") {
    const trigger = () => void flushQueueTelemetry();
    window.addEventListener("online", trigger);
    window.addEventListener("focus", trigger);
    if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") void flushQueueTelemetry();
        });
    }
    // A buffer can survive from a prior session (the flush that would have sent
    // it was interrupted); try once on load.
    void flushQueueTelemetry();
}

/** Test-only: clear the buffer + in-flight guard between cases. */
export function __resetQueueTelemetryForTests(): void {
    try {
        localStorage.removeItem(BUFFER_KEY);
    } catch {
        /* ignore */
    }
    flushInFlight = false;
}

/** Test-only: read the current buffer. */
export function __readQueueTelemetryBuffer(): QueueEvent[] {
    return readBuffer();
}
